import { acceptHMRUpdate, defineStore } from 'pinia';
import { http as axios } from '@/services/http.service';

export interface DaySchedule {
  day: number; // 0-6 (Sun-Sat)
  timeSlots: string[]; // ["HH:MM", ...]
}

export interface DynamicDaySchedule {
  date: string; // "YYYY-MM-DD"
  timeSlots: string[]; // ["HH:MM", ...]
}

export type ScheduleMode = 'weekly' | 'dynamic';

interface AvailabilityState {
  lastLoadedProfessionalId: number | null;
  schedule: DaySchedule[];
  scheduleMode: ScheduleMode;
  scheduleDaysAhead: number;
  dynamicSchedule: DynamicDaySchedule[];
  loading: boolean;
  rangeCounts: Record<string, number>; // map of YYYY-MM-DD -> available starting slots count
  nextSlots: Array<{ datetime: string; time: string; date: string }>;
}

// Availability endpoints, addressed by base path.
//
// The panel serves every brand from the apex, where `/api/v1/professionals`
// answers from `public.professionals` -- a different person who happens to share
// the id. A brand therefore passes its own prefix, which already carries the
// professional id, so the id is in the path instead of the query string.
//
// The availability route names the professional as well
// (`/api/v1/tenants/:tenant_id/professionals/:professional_id/availability`), so
// a brand prefix that omitted the id answered 404 and the Agenda never loaded.
const DEFAULT_BASE_PATH = '/api/v1/professionals';
function availabilityUrl(basePath?: string, professionalId?: number | null): string {
  const prefix = basePath || DEFAULT_BASE_PATH;
  if (basePath) {
    // The brand panel already builds `actionBasePath` as
    // `/api/v1/tenants/:id/professionals/:professional_id`. Don't append the
    // professional id again, or every save/load would hit
    // `.../professionals/:id/:id/availability` and 404.
    if (professionalId && prefix.replace(/\/$/, '').endsWith(`/${professionalId}`)) {
      return `${prefix.replace(/\/$/, '')}/availability`;
    }
    if (professionalId) return `${prefix.replace(/\/$/, '')}/${professionalId}/availability`;
    return `${prefix.replace(/\/$/, '')}/availability`;
  }
  // Apex uses the collection route `/api/v1/professionals/availability` and
  // passes `professional_id` as a query parameter (see ProfessionalAvailabilityController).
  return DEFAULT_BASE_PATH + '/availability';
}
// The per-professional endpoints (`.../available_slots_range`, `.../next_available_slots`)
// take the professional id in the path, so a brand prefix slots in before it.
function professionalUrl(professionalId: number, path: string, basePath?: string): string {
  const prefix = basePath || DEFAULT_BASE_PATH;
  if (basePath) {
    if (prefix.replace(/\/$/, '').endsWith(`/${professionalId}`)) {
      return `${prefix.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
    }
    return `${prefix.replace(/\/$/, '')}/${professionalId}${
      path.startsWith('/') ? path : `/${path}`
    }`;
  }
  return `${prefix}/${professionalId}${path.startsWith('/') ? path : `/${path}`}`;
}
function availabilityParams(professionalId?: number | null, basePath?: string) {
  // A scoped base path already names the professional; repeating it as a query
  // parameter would be redundant and misleading.
  if (basePath) return undefined;
  return professionalId ? { professional_id: professionalId } : undefined;
}

export const useAvailabilityStore = defineStore('availability.store', {
  state: (): AvailabilityState => ({
    lastLoadedProfessionalId: null,
    schedule: [],
    scheduleMode: 'weekly',
    scheduleDaysAhead: 15,
    dynamicSchedule: [],
    loading: false,
    rangeCounts: {},
    nextSlots: [],
  }),
  actions: {
    async fetchAvailability(
      professionalId?: number | null,
      basePath?: string,
    ): Promise<DaySchedule[]> {
      try {
        this.loading = true;
        const response = await axios.get(availabilityUrl(basePath, professionalId), {
          params: availabilityParams(professionalId, basePath),
        });
        const schedule = (response.data?.schedule ?? []) as DaySchedule[];
        this.schedule = Array.isArray(schedule) ? schedule : [];

        const mode = response.data?.schedule_mode as ScheduleMode | undefined;
        this.scheduleMode = mode === 'dynamic' ? 'dynamic' : 'weekly';
        this.scheduleDaysAhead =
          Number(response.data?.schedule_days_ahead) > 0
            ? Number(response.data?.schedule_days_ahead)
            : 15;
        const dynamic = (response.data?.dynamic_schedule ?? []) as DynamicDaySchedule[];
        this.dynamicSchedule = Array.isArray(dynamic) ? dynamic : [];

        this.lastLoadedProfessionalId = professionalId ?? null;
        return this.schedule;
      } finally {
        this.loading = false;
      }
    },

    async saveAvailability(
      professionalId: number | null | undefined,
      schedule: DaySchedule[],
      basePath?: string,
    ): Promise<DaySchedule[]> {
      const response = await axios.post(
        availabilityUrl(basePath, professionalId),
        { availability: schedule },
        { params: availabilityParams(professionalId, basePath) },
      );
      const saved = (response.data?.schedule ?? []) as DaySchedule[];
      this.schedule = Array.isArray(saved) ? saved : [];
      this.lastLoadedProfessionalId = professionalId ?? null;
      return this.schedule;
    },

    // Save weekly schedule together with mode + days-ahead configuration
    async saveWeeklySchedule(
      professionalId: number | null | undefined,
      schedule: DaySchedule[],
      scheduleMode: ScheduleMode = 'weekly',
      scheduleDaysAhead = 15,
      basePath?: string,
    ): Promise<void> {
      const response = await axios.post(
        availabilityUrl(basePath, professionalId),
        {
          availability: schedule,
          schedule_mode: scheduleMode,
          schedule_days_ahead: scheduleDaysAhead,
        },
        { params: availabilityParams(professionalId, basePath) },
      );
      const saved = (response.data?.schedule ?? []) as DaySchedule[];
      this.schedule = Array.isArray(saved) ? saved : [];
      this.scheduleMode = scheduleMode;
      this.scheduleDaysAhead = scheduleDaysAhead;
      this.lastLoadedProfessionalId = professionalId ?? null;
    },

    // Save dynamic per-date schedule together with mode configuration
    async saveDynamicSchedule(
      professionalId: number | null | undefined,
      dynamicSchedule: DynamicDaySchedule[],
      scheduleDaysAhead = 15,
      basePath?: string,
    ): Promise<void> {
      const response = await axios.post(
        availabilityUrl(basePath, professionalId),
        {
          schedule_mode: 'dynamic',
          schedule_days_ahead: scheduleDaysAhead,
          dynamic_availability_schedule: dynamicSchedule,
        },
        { params: availabilityParams(professionalId, basePath) },
      );
      const saved = (response.data?.dynamic_schedule ?? []) as DynamicDaySchedule[];
      this.dynamicSchedule = Array.isArray(saved) ? saved : [];
      this.scheduleMode = 'dynamic';
      this.scheduleDaysAhead = scheduleDaysAhead;
      this.lastLoadedProfessionalId = professionalId ?? null;
    },

    // Apply incoming schedule updates from WebSocket broadcasts (AVAILABILITY_UPDATED)
    applyIncomingSchedule(
      professionalId: number,
      schedule: DaySchedule[],
      extra?: {
        scheduleMode?: ScheduleMode;
        scheduleDaysAhead?: number;
        dynamicSchedule?: DynamicDaySchedule[];
      },
    ) {
      console.log(
        `[AVAILABILITY_UPDATED] Received schedule update for professional ${professionalId}:`,
        schedule,
      );

      if (this.lastLoadedProfessionalId === professionalId) {
        console.log(
          `[AVAILABILITY_UPDATED] Applying schedule update - previous schedule:`,
          this.schedule,
        );
        this.schedule = Array.isArray(schedule) ? schedule : [];

        if (extra) {
          if (extra.scheduleMode === 'weekly' || extra.scheduleMode === 'dynamic') {
            this.scheduleMode = extra.scheduleMode;
          }
          if (typeof extra.scheduleDaysAhead === 'number' && extra.scheduleDaysAhead > 0) {
            this.scheduleDaysAhead = extra.scheduleDaysAhead;
          }
          if (Array.isArray(extra.dynamicSchedule)) {
            this.dynamicSchedule = extra.dynamicSchedule;
          }
        }

        console.log(
          `[AVAILABILITY_UPDATED] Schedule updated successfully - new schedule:`,
          this.schedule,
        );
      } else {
        console.warn(
          `[AVAILABILITY_UPDATED] Ignoring update - professional mismatch: expected ${this.lastLoadedProfessionalId}, got ${professionalId}`,
        );
      }
    },

    // Apply incoming SLOT_OCCUPIED from WebSocket - removes occupied slots in real-time
    // Uses UTC methods since backend sends ISO8601 in UTC
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    applySlotOccupied(professionalId: number, payload: Record<string, any>) {
      if (this.lastLoadedProfessionalId !== professionalId) return;

      const datetime = payload.datetime as string;
      const durationBlocks = (payload.duration_blocks as number) || 1;
      if (!datetime) return;

      const dt = new Date(datetime);
      // Use UTC methods since the datetime is in UTC (ISO8601)
      const dayOfWeek = dt.getUTCDay();
      const startMinutes = dt.getUTCHours() * 60 + dt.getUTCMinutes();
      const blockSize = 30; // minutes per block

      // Calculate which slots to remove (start slot + consecutive blocks)
      const slotsToRemove: string[] = [];
      for (let i = 0; i < durationBlocks; i++) {
        const blockMinutes = startMinutes + i * blockSize;
        const hours = Math.floor(blockMinutes / 60);
        const mins = blockMinutes % 60;
        slotsToRemove.push(
          `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`,
        );
      }

      // Update schedule - remove occupied slots
      this.schedule = this.schedule.map((day) => {
        if (day.day !== dayOfWeek) return day;
        return {
          ...day,
          timeSlots: day.timeSlots.filter((slot) => !slotsToRemove.includes(slot)),
        };
      });

      // Remove from nextSlots if present (match by time and UTC date)
      const slotTime = slotsToRemove[0];
      // Extract date part from ISO string directly to avoid timezone conversion
      const dateStr = datetime.slice(0, 10); // YYYY-MM-DD from ISO string
      this.nextSlots = this.nextSlots.filter(
        (slot) => !(slot.time === slotTime && slot.date === dateStr),
      );

      // Decrement rangeCounts for this date
      if (this.rangeCounts[dateStr] && this.rangeCounts[dateStr] > 0) {
        this.rangeCounts[dateStr] -= 1;
      }
    },

    // Apply incoming SLOT_AVAILABLE from WebSocket - adds available slots in real-time
    // Uses UTC methods since backend sends ISO8601 in UTC
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    applySlotAvailable(professionalId: number, payload: Record<string, any>) {
      if (this.lastLoadedProfessionalId !== professionalId) return;

      const datetime = payload.datetime as string;
      const durationBlocks = (payload.duration_blocks as number) || 1;
      if (!datetime) return;

      const dt = new Date(datetime);
      // Use UTC methods since the datetime is in UTC (ISO8601)
      const dayOfWeek = dt.getUTCDay();
      const startMinutes = dt.getUTCHours() * 60 + dt.getUTCMinutes();
      const blockSize = 30; // minutes per block

      // Calculate which slots to add (start slot + consecutive blocks)
      const slotsToAdd: string[] = [];
      for (let i = 0; i < durationBlocks; i++) {
        const blockMinutes = startMinutes + i * blockSize;
        const hours = Math.floor(blockMinutes / 60);
        const mins = blockMinutes % 60;
        slotsToAdd.push(`${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`);
      }

      // Update schedule - add available slots
      this.schedule = this.schedule.map((day) => {
        if (day.day !== dayOfWeek) return day;
        // Merge new slots with existing, removing duplicates and sorting
        const mergedSlots = [...new Set([...day.timeSlots, ...slotsToAdd])].sort();
        return {
          ...day,
          timeSlots: mergedSlots,
        };
      });

      // Add to nextSlots if date is in range
      const slotTime = slotsToAdd[0];
      const dateStr = datetime.slice(0, 10); // YYYY-MM-DD from ISO string

      // Check if this slot should be in nextSlots (is it in the future?)
      const slotDateTime = new Date(datetime);
      if (slotDateTime > new Date()) {
        const existingSlot = this.nextSlots.find((s) => s.time === slotTime && s.date === dateStr);
        if (!existingSlot) {
          this.nextSlots.push({
            datetime: datetime,
            time: slotTime,
            date: dateStr,
          });
          // Sort nextSlots by datetime
          this.nextSlots.sort(
            (a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime(),
          );
        }
      }

      // Increment rangeCounts for this date
      if (!this.rangeCounts[dateStr]) {
        this.rangeCounts[dateStr] = 0;
      }
      this.rangeCounts[dateStr] += 1;
    },

    async fetchRangeCounts(
      professionalId: number,
      startISO: string,
      days = 10,
      specialtyId?: number | null,
      excludeAppointmentId?: number | null,
      basePath?: string,
    ): Promise<Record<string, number>> {
      try {
        const { data } = await axios.get(
          professionalUrl(professionalId, '/available_slots_range', basePath),
          {
            params: {
              start: startISO,
              days,
              specialty_id: specialtyId ?? undefined,
              exclude_appointment_id: excludeAppointmentId ?? undefined,
            },
          },
        );
        const items = Array.isArray(data?.items)
          ? (data.items as Array<{ date: string; count: number }>)
          : [];
        const map: Record<string, number> = {};
        for (const it of items) {
          const k = String(it.date);
          map[k] = Number(it.count || 0);
        }
        this.rangeCounts = map;
        return this.rangeCounts;
      } catch (error) {
        // Clear stale counts so UIs never show availability for uncommitted ranges
        // (e.g. a start date beyond the booking horizon rejected by the backend).
        this.rangeCounts = {};
        throw error;
      }
    },
    async fetchNextSlots(
      professionalId: number,
      specialtyId?: number | null,
      limit = 10,
      excludeAppointmentId?: number | null,
      daysAhead?: number | null,
      basePath?: string,
    ): Promise<void> {
      try {
        const response = await axios.get(
          professionalUrl(professionalId, '/next_available_slots', basePath),
          {
            params: {
              limit,
              specialty_id: specialtyId ?? undefined,
              exclude_appointment_id: excludeAppointmentId ?? undefined,
              days_ahead: daysAhead ?? undefined,
            },
          },
        );
        this.nextSlots = response.data.slots || [];
        this.lastLoadedProfessionalId = professionalId;
      } catch (error) {
        console.error('Error fetching next slots in store:', error);
        this.nextSlots = [];
      }
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAvailabilityStore, import.meta.hot));
}
